// Nomina unica de todas las plantas (PP-70): logica pura, sin React ni reloj.
// La API manda cada integrante de la nomina una vez, con una habilitacion por
// planta (GET /provider/roster, ADR-025). Aca se arman las fichas por planta
// ("Planta A ✓ · Planta B ✕ antigüedad"), el filtro "No elegibles" y la
// fila que la tabla de la nomina (RosterPanel) sabe pintar.
//
// Las escrituras siguen siendo por planta (/sites/{siteId}/...): la baja, la
// reincorporacion y la edicion valen para toda la empresa madre, asi que van
// por la primera habilitacion de la fila (su ancla) sin preguntar la planta.
import type {
  CountByDocumentStatus,
  Driver,
  IneligibilityReason,
  ProviderRosterDriver,
  ProviderRosterResponse,
  ProviderRosterSite,
  ProviderRosterVehicle,
  SiteVehicle,
} from "@ucorp/contracts";
import type { FleetSite } from "../fleet/port.js";
import { documentTypeLabel, ineligibilityReasonLabel } from "./documents.js";

type Entry = ProviderRosterVehicle | ProviderRosterDriver;
type Enablement = Entry["sites"][number];

/** Una ficha por planta habilitada de la fila. */
export interface SiteChip {
  readonly siteId: string;
  readonly providerId: string;
  readonly siteName: string;
  readonly eligible: boolean;
  /** "antigüedad", "falta VTV"...; vacio si es elegible. */
  readonly short: string;
  /** Los motivos completos, para el detalle accesible. */
  readonly detail: string;
}

/** Fila de la nomina unica con lo que la tabla y las escrituras necesitan. */
export interface AllSitesRow<E extends Entry> {
  /** Unico en la lista: planta, proveedor e id local del ancla. */
  readonly id: string;
  readonly code: string;
  readonly inRoster: boolean;
  /** Habilitacion por la que van las escrituras (vale para toda la empresa). */
  readonly anchor: FleetSite & { readonly id: string };
  readonly entry: E;
}

export type AllSitesVehicleRow = AllSitesRow<ProviderRosterVehicle>;
export type AllSitesDriverRow = AllSitesRow<ProviderRosterDriver>;

/** Motivo corto para la ficha: lo que hay que mirar, no la explicacion. */
export function shortReason(reason: IneligibilityReason): string {
  switch (reason.code) {
    case "chain_inactive":
      return "fuera de servicio";
    case "blocked_by_site":
      return "bloqueado";
    case "missing_document":
      return `falta ${documentTypeLabel(reason.documentType)}`;
    case "expired_document":
      return `vencido: ${documentTypeLabel(reason.documentType)}`;
    case "vehicle_too_old":
      return "antigüedad";
    case "model_year_unknown":
      return "sin año del modelo";
  }
}

/** Fichas en el orden de las plantas de la respuesta (por nombre). */
export function siteChips(entry: Entry, sites: readonly ProviderRosterSite[]): SiteChip[] {
  const order = (e: Enablement) => sites.findIndex((s) => s.siteId === e.siteId && s.providerId === e.providerId);
  return [...entry.sites]
    .filter((e) => order(e) !== -1)
    .sort((a, b) => order(a) - order(b))
    .map((e) => ({
      siteId: e.siteId,
      providerId: e.providerId,
      siteName: sites[order(e)]!.siteName,
      eligible: e.eligibility.eligible,
      short: [...new Set(e.eligibility.reasons.map(shortReason))].join(", "),
      detail: e.eligibility.reasons.map(ineligibilityReasonLabel).join("; "),
    }));
}

/** "Planta A ✓" o "Planta B ✕ antigüedad", el texto de una ficha. */
export function chipText(chip: SiteChip): string {
  return chip.eligible ? `${chip.siteName} ✓` : `${chip.siteName} ✕ ${chip.short}`;
}

/** No elegible en alguna de sus plantas: lo que muestra el filtro "No elegibles". */
export function ineligibleSomewhere(entry: Entry): boolean {
  return entry.sites.some((e) => !e.eligibility.eligible);
}

export function onlyIneligible<E extends Entry>(rows: readonly AllSitesRow<E>[]): AllSitesRow<E>[] {
  return rows.filter((row) => ineligibleSomewhere(row.entry));
}

/** "2 vigentes · 1 vencido"; "Sin documentos" si no tiene ninguno. */
export function documentsSummary(count: CountByDocumentStatus): string {
  const parts: string[] = [];
  const add = (n: number, one: string, many: string) => {
    if (n > 0) parts.push(`${String(n)} ${n === 1 ? one : many}`);
  };
  add(count.expired, "vencido", "vencidos");
  add(count.expiring_soon, "por vencer", "por vencer");
  add(count.effective, "vigente", "vigentes");
  add(count.no_expiry, "sin vencimiento", "sin vencimiento");
  return parts.length === 0 ? "Sin documentos" : parts.join(" · ");
}

function rowOf<E extends Entry>(entry: E, sites: readonly ProviderRosterSite[]): AllSitesRow<E> | undefined {
  // El ancla es la primera habilitacion en el orden de las plantas.
  const [first] = siteChips(entry, sites);
  const anchor = first === undefined ? undefined : entry.sites.find((e) => e.siteId === first.siteId && e.providerId === first.providerId);
  if (anchor === undefined) return undefined;
  return {
    id: `${anchor.siteId}/${anchor.providerId}/${anchor.id}`,
    code: entry.code,
    inRoster: entry.inRoster,
    anchor: { siteId: anchor.siteId, providerId: anchor.providerId, id: anchor.id },
    entry,
  };
}

/**
 * Filas de la respuesta. Una habilitacion de una planta que no vino en
 * `sites` se descarta (defensa: la API ya filtra por la cuenta), y una fila
 * sin ninguna habilitacion conocida no se muestra.
 */
export function allSitesRows(roster: ProviderRosterResponse): { vehicles: AllSitesVehicleRow[]; drivers: AllSitesDriverRow[] } {
  const known = (e: Enablement) => roster.sites.some((s) => s.siteId === e.siteId && s.providerId === e.providerId);
  const clean = <E extends Entry>(entry: E): E => ({ ...entry, sites: entry.sites.filter(known) });
  return {
    vehicles: roster.vehicles.map((v) => rowOf(clean(v), roster.sites)).filter((r) => r !== undefined),
    drivers: roster.drivers.map((d) => rowOf(clean(d), roster.sites)).filter((r) => r !== undefined),
  };
}

function anchorEnablement(row: AllSitesRow<Entry>): Enablement {
  return row.entry.sites.find((e) => e.siteId === row.anchor.siteId && e.providerId === row.anchor.providerId)!;
}

/** El vehiculo como lo ve su planta ancla, para el formulario de edicion. */
export function anchorVehicle(row: AllSitesVehicleRow): SiteVehicle {
  const { entry } = row;
  const enablement = anchorEnablement(row);
  return {
    siteId: row.anchor.siteId,
    providerId: row.anchor.providerId,
    id: row.anchor.id,
    code: entry.code,
    type: entry.type,
    capacity: entry.capacity,
    plate: entry.plate,
    ...(entry.year === undefined ? {} : { year: entry.year }),
    holder: entry.holder,
    active: enablement.active,
    inRoster: entry.inRoster,
    eligibility: enablement.eligibility,
  };
}

export function anchorDriver(row: AllSitesDriverRow): Driver {
  const { entry } = row;
  const enablement = anchorEnablement(row);
  return {
    siteId: row.anchor.siteId,
    providerId: row.anchor.providerId,
    id: row.anchor.id,
    code: entry.code,
    name: entry.name,
    ...(entry.idDocument === undefined ? {} : { idDocument: entry.idDocument }),
    ...(entry.phone === undefined ? {} : { phone: entry.phone }),
    holder: entry.holder,
    active: enablement.active,
    inRoster: entry.inRoster,
    eligibility: enablement.eligibility,
  };
}

/** Lo que una escritura devolvio en la planta ancla, sobre la fila; la relectura trae el resto. */
function patchAnchor<E extends Entry>(row: AllSitesRow<E>, written: { siteId: string; providerId: string; id: string; inRoster: boolean; active: boolean; eligibility: Enablement["eligibility"] }, global: Partial<E>): AllSitesRow<E> {
  const sites = row.entry.sites.map((e) =>
    e.siteId === written.siteId && e.providerId === written.providerId && e.id === written.id
      ? { ...e, active: written.active, eligibility: written.eligibility }
      : e,
  );
  const entry = { ...row.entry, ...global, inRoster: written.inRoster, sites } as E;
  return { ...row, code: entry.code, inRoster: entry.inRoster, entry };
}

export function vehicleRowAfterWrite(row: AllSitesVehicleRow, written: SiteVehicle): AllSitesVehicleRow {
  const { code, type, capacity, plate, year } = written;
  return patchAnchor(row, written, { code, type, capacity, plate, ...(year === undefined ? {} : { year }) });
}

export function driverRowAfterWrite(row: AllSitesDriverRow, written: Driver): AllSitesDriverRow {
  const { code, name, idDocument, phone } = written;
  return patchAnchor(row, written, {
    code,
    name,
    ...(idDocument === undefined ? {} : { idDocument }),
    ...(phone === undefined ? {} : { phone }),
  });
}

const NO_DOCUMENTS: CountByDocumentStatus = { effective: 0, expiring_soon: 0, expired: 0, no_expiry: 0 };

/** Fila provisoria de un alta, con la habilitacion donde se hizo; la relectura suma las otras plantas. */
export function vehicleRowFromCreate(written: SiteVehicle): AllSitesVehicleRow {
  const { siteId, providerId, id, active, eligibility, code, type, capacity, plate, year, holder, inRoster } = written;
  const entry: ProviderRosterVehicle = {
    code, type, capacity, plate, ...(year === undefined ? {} : { year }),
    holder: holder ?? { brandName: "", relation: "contractor" },
    inRoster, documents: NO_DOCUMENTS, sites: [{ siteId, providerId, id, active, eligibility }],
  };
  return { id: `${siteId}/${providerId}/${id}`, code, inRoster, anchor: { siteId, providerId, id }, entry };
}

export function driverRowFromCreate(written: Driver): AllSitesDriverRow {
  const { siteId, providerId, id, active, eligibility, code, name, idDocument, phone, holder, inRoster } = written;
  const entry: ProviderRosterDriver = {
    code, name, ...(idDocument === undefined ? {} : { idDocument }), ...(phone === undefined ? {} : { phone }),
    holder: holder ?? { brandName: "", relation: "contractor" },
    inRoster, documents: NO_DOCUMENTS, sites: [{ siteId, providerId, id, active, eligibility }],
  };
  return { id: `${siteId}/${providerId}/${id}`, code, inRoster, anchor: { siteId, providerId, id }, entry };
}
